import React, { useEffect, useRef } from 'react';
import { useTracks, useRoomContext } from '@livekit/components-react';
import { RemoteTrackPublication, Track as LiveKitTrack, Room } from 'livekit-client';

export interface SelectiveAudioRendererProps {
  /**
   * Which audio channels to subscribe to
   * 'llm' - only LLM audio (audio_channel_llm_to_gpu)
   * 'gpu' - only GPU audio (audio) 
   * 'both' - both audio channels
   * 'default' - use default LiveKit behavior (all audio tracks)
   */
  audioChannels?: 'llm' | 'gpu' | 'both' | 'default';
  /**
   * Volume for LLM audio channel (0-1)
   */
  llmVolume?: number;
  /**
   * Volume for GPU audio channel (0-1)
   */
  gpuVolume?: number;
}

export function SelectiveAudioRenderer({ 
  audioChannels = 'default',
  llmVolume = 1.0,
  gpuVolume = 1.0 
}: SelectiveAudioRendererProps) {
  const room = useRoomContext();
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  
  // Get all audio tracks
  const audioTracks = useTracks(
    [{ source: LiveKitTrack.Source.Microphone, withPlaceholder: false }],
    { onlySubscribed: false }
  );

  useEffect(() => {
    if (!room || audioChannels === 'default') {
      // Use default LiveKit behavior - don't interfere
      return;
    }

    const handleTrackSubscribed = (track: LiveKitTrack, publication: RemoteTrackPublication) => {
      if (track.kind !== LiveKitTrack.Kind.Audio) return;

      const trackName = publication.trackName;
      
      console.log('SelectiveAudioRenderer: Audio track subscribed:', { trackName });

      // Determine if we should handle this track based on track name
      let shouldHandle = false;
      let volume = 1.0;
      
      if (audioChannels === 'llm' && trackName === 'audio_channel_llm_to_gpu') {
        shouldHandle = true;
        volume = llmVolume;
      } else if (audioChannels === 'gpu' && trackName === 'audio') {
        shouldHandle = true;
        volume = gpuVolume;
      } else if (audioChannels === 'both') {
        if (trackName === 'audio_channel_llm_to_gpu') {
          shouldHandle = true;
          volume = llmVolume;
        } else if (trackName === 'audio') {
          shouldHandle = true;
          volume = gpuVolume;
        }
      }

      if (shouldHandle) {
        // Create audio element for this specific track
        const audioElement = document.createElement('audio');
        audioElement.autoplay = true;
        audioElement.volume = volume;
        audioElement.style.display = 'none';
        
        // Attach the track to the audio element
        track.attach(audioElement);
        
        // Store reference
        audioElementsRef.current.set(track.sid || '', audioElement);
        document.body.appendChild(audioElement);
        
        console.log(`SelectiveAudioRenderer: Attached ${trackName} with volume ${volume}`);
      } else {
        console.log(`SelectiveAudioRenderer: Skipping track ${trackName} (not in selected channels)`);
      }
    };

    const handleTrackUnsubscribed = (track: LiveKitTrack) => {
      if (track.kind !== LiveKitTrack.Kind.Audio) return;
      
      const audioElement = audioElementsRef.current.get(track.sid || '');
      if (audioElement) {
        track.detach(audioElement);
        try {
          document.body.removeChild(audioElement);
        } catch (e) {
          console.warn('SelectiveAudioRenderer: Could not remove audio element:', e);
        }
        audioElementsRef.current.delete(track.sid || '');
        console.log('SelectiveAudioRenderer: Detached audio track', track.sid);
      }
    };

    // Listen for track events
    room.on('trackSubscribed', handleTrackSubscribed);
    room.on('trackUnsubscribed', handleTrackUnsubscribed);

    // Handle existing tracks
    room.remoteParticipants.forEach((participant: any) => {
      participant.audioTrackPublications.forEach((publication: any) => {
        if (publication.track) {
          handleTrackSubscribed(publication.track, publication);
        }
      });
    });

    return () => {
      room.off('trackSubscribed', handleTrackSubscribed);
      room.off('trackUnsubscribed', handleTrackUnsubscribed);
      
      // Clean up all audio elements
      audioElementsRef.current.forEach((audioElement, trackSid) => {
        try {
          document.body.removeChild(audioElement);
        } catch (e) {
          // Element might already be removed
        }
      });
      audioElementsRef.current.clear();
    };
  }, [room, audioChannels, llmVolume, gpuVolume]);

  // Update volumes when they change
  useEffect(() => {
    audioElementsRef.current.forEach((audioElement, trackSid) => {
      const allPublications: any[] = [];
      room?.remoteParticipants.forEach((p: any) => {
        allPublications.push(...Array.from(p.audioTrackPublications.values()));
      });
      
      const track = allPublications.find((pub: any) => pub.track?.sid === trackSid);
      
      if (track) {
        const trackName = track.trackName;
        
        if (trackName === 'audio_channel_llm_to_gpu') {
          audioElement.volume = llmVolume;
        } else if (trackName === 'audio') {
          audioElement.volume = gpuVolume;
        }
      }
    });
  }, [room, llmVolume, gpuVolume]);

  // Don't render anything - this component only manages audio
  return null;
}
